import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPrint, faCheckCircle, faClock, faTruck } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid ${props => props.theme.colors.greyBackground};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid ${props => props.theme.colors.greyBackground};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const OrderNumber = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  background: ${props => {
    if (props.$status === 'completed') return '#4CAF50';
    if (props.$status === 'ready-to-collect') return '#FF9800';
    if (props.$status === 'processing') return '#2196F3';
    return '#9E9E9E';
  }};
  color: white;
  margin-top: 0.5rem;
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const InfoItem = styled.div`
  padding: 1rem;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.offWhite};
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  border-bottom: 2px solid ${props => props.theme.colors.greyBackground};
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  vertical-align: top;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  background: ${props => props.theme.colors.greyBackground};
`;

const ItemName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const ItemDetails = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const TotalsSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid ${props => props.theme.colors.greyBackground};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  font-size: 1rem;
  
  &.total {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.greyBackground};
    margin-top: 0.5rem;
  }
`;

const PickupInfo = styled.div`
  background: #fff3e0;
  border-left: 4px solid #FF9800;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #F44336;
`;

const FarmShopOrderDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState('');

  useEffect(() => {
    // If order not in state, try to load from URL params (order_key)
    if (!order) {
      const orderKey = searchParams.get('key');
      const orderId = searchParams.get('order_id');
      const orderEmail = searchParams.get('email');
      
      if (orderKey || orderId || orderEmail) {
        loadOrderFromKey(orderKey, orderId, orderEmail);
      } else {
        setError('Order not found. Please use the order lookup page.');
        setLoading(false);
      }
    }
  }, [searchParams]);

  const loadOrderFromKey = async (orderKey, orderId, orderEmail) => {
    setLoading(true);
    setError('');

    try {
      const getApiUrl = () => {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return 'http://localhost:3000/api/farm-order-lookup.php';
        } else if (hostname.includes('staging')) {
          return 'https://staging.belvoircastle.com/api/farm-order-lookup.php';
        } else {
          return 'https://www.belvoircastle.com/api/farm-order-lookup.php';
        }
      };

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_key: orderKey,
          order_id: orderId,
          email: orderEmail,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Order not found');
      }

      setOrder(data.order);
    } catch (err) {
      setError(err.message || 'Unable to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return faCheckCircle;
    if (status === 'ready-to-collect') return faTruck;
    if (status === 'processing') return faClock;
    return faClock;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'completed': 'Completed',
      'ready-to-collect': 'Ready to Collect',
      'processing': 'Processing',
      'pending': 'Pending',
      'on-hold': 'On Hold',
      'cancelled': 'Cancelled',
      'refunded': 'Refunded',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Container>
        <ContentContainer>
          <Card>
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>Loading order details...</p>
            </div>
          </Card>
        </ContentContainer>
        <Footer />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container>
        <ContentContainer>
          <BackButton onClick={() => navigate('/farm-shop/order')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Order Lookup
          </BackButton>
          <Card>
            <ErrorMessage>
              <p>{error || 'Order not found'}</p>
            </ErrorMessage>
          </Card>
        </ContentContainer>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      <ContentContainer>
        <BackButton onClick={() => navigate('/farm-shop')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Farm Shop
        </BackButton>

        <Card>
          <Header>
            <HeaderLeft>
              <Title>Order Details</Title>
              <OrderNumber>Order #{order.order_number}</OrderNumber>
              <StatusBadge $status={order.status}>
                <FontAwesomeIcon icon={getStatusIcon(order.status)} />
                {getStatusLabel(order.status)}
              </StatusBadge>
            </HeaderLeft>
            <PrintButton onClick={handlePrint}>
              <FontAwesomeIcon icon={faPrint} />
              Print Receipt
            </PrintButton>
          </Header>

          <Section>
            <SectionTitle>Order Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Order Date</InfoLabel>
                <InfoValue>{order.date_created_formatted}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Payment Method</InfoLabel>
                <InfoValue>{order.payment_method_title || order.payment_method || 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Total Amount</InfoLabel>
                <InfoValue>{order.total_formatted}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Section>
            <SectionTitle>Items</SectionTitle>
            <ItemsTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Product</TableHeaderCell>
                  <TableHeaderCell style={{ textAlign: 'right' }}>Quantity</TableHeaderCell>
                  <TableHeaderCell style={{ textAlign: 'right' }}>Price</TableHeaderCell>
                </tr>
              </TableHeader>
              <tbody>
                {order.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {item.image && (
                          <ItemImage src={item.image} alt={item.name} />
                        )}
                        <div>
                          <ItemName>{item.name}</ItemName>
                          {item.variation_id && (
                            <ItemDetails>Variation ID: {item.variation_id}</ItemDetails>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{item.quantity}</TableCell>
                    <TableCell style={{ textAlign: 'right', fontWeight: 600 }}>
                      {item.price_formatted}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </ItemsTable>

            <TotalsSection>
              <TotalRow>
                <span>Subtotal</span>
                <span>{order.totals.subtotal_formatted}</span>
              </TotalRow>
              {order.totals.shipping > 0 && (
                <TotalRow>
                  <span>Shipping</span>
                  <span>{order.totals.shipping_formatted}</span>
                </TotalRow>
              )}
              {order.totals.tax > 0 && (
                <TotalRow>
                  <span>Tax</span>
                  <span>{order.totals.tax_formatted}</span>
                </TotalRow>
              )}
              <TotalRow className="total">
                <span>Total</span>
                <span>{order.totals.total_formatted}</span>
              </TotalRow>
            </TotalsSection>
          </Section>

          {order.pickup && (
            <Section>
              <SectionTitle>Collection Details</SectionTitle>
              {order.pickup.date && (
                <InfoItem>
                  <InfoLabel>Collection Date</InfoLabel>
                  <InfoValue>{order.pickup.date_formatted}</InfoValue>
                </InfoItem>
              )}
              {order.pickup.notes && (
                <PickupInfo>
                  <strong>Collection Notes:</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>{order.pickup.notes}</p>
                </PickupInfo>
              )}
            </Section>
          )}

          <Section>
            <SectionTitle>Billing Address</SectionTitle>
            <InfoItem>
              <InfoValue>
                {order.billing.first_name} {order.billing.last_name}<br />
                {order.billing.email && <>{order.billing.email}<br /></>}
                {order.billing.phone && <>{order.billing.phone}<br /></>}
                {order.billing.address_1}<br />
                {order.billing.address_2 && <>{order.billing.address_2}<br /></>}
                {order.billing.city} {order.billing.postcode}<br />
                {order.billing.country}
              </InfoValue>
            </InfoItem>
          </Section>

          {order.customer_note && (
            <Section>
              <SectionTitle>Order Notes</SectionTitle>
              <InfoItem>
                <InfoValue>{order.customer_note}</InfoValue>
              </InfoItem>
            </Section>
          )}
        </Card>
      </ContentContainer>
      <Footer />
    </Container>
  );
};

export default FarmShopOrderDetails;

